/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ReactNode, useEffect, useRef } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { useSpaceStore } from '@coze-foundation/space-store';
import { Space, Skeleton } from '@coze-arch/coze-design';

import { type IWorkspaceListItem } from './components/workspace-list-item';
import {
  type IWorkspaceMenuGroup,
  WorkspaceList,
} from './components/workspace-list';
import { FavoritesList } from './components/favorites-list';

import './components/list.css';

interface IWorkspaceSubMenuProps {
  header: ReactNode;
  menus?: Array<IWorkspaceListItem>;
  menuGroups?: Array<IWorkspaceMenuGroup>;
  currentSubMenu?: string;
}

export const WorkspaceSubMenu = ({
  header,
  menus,
  menuGroups,
  currentSubMenu,
}: IWorkspaceSubMenuProps) => {
  const { spaceList, loading } = useSpaceStore(
    useShallow(state => ({
      currentSpace: state.space,
      spaceList: state.spaceList,
      loading: !!state.loading || !state.inited,
    })),
  );

  const hasSpace = spaceList.length > 0;
  const navigationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!currentSubMenu || !navigationRef.current) {
      return;
    }

    const navigation = navigationRef.current;
    const activeMenu = document.getElementById(
      `workspace-submenu-${currentSubMenu}`,
    );
    if (!activeMenu || !navigation.contains(activeMenu)) {
      return;
    }

    const navigationRect = navigation.getBoundingClientRect();
    const activeMenuRect = activeMenu.getBoundingClientRect();
    const isOutsideViewport =
      activeMenuRect.top < navigationRect.top ||
      activeMenuRect.bottom > navigationRect.bottom;

    if (isOutsideViewport) {
      activeMenu.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [currentSubMenu, menuGroups, menus]);

  return (
    <Skeleton loading={loading} active placeholder={<Skeleton.Paragraph />}>
      <Space
        spacing={4}
        vertical
        className="workspace-submenu-root w-full h-full min-h-0 overflow-hidden"
      >
        <div className="flex-none w-full">{header}</div>
        {hasSpace ? (
          <>
            <div
              ref={navigationRef}
              className="workspace-submenu-navigation w-full"
            >
              <WorkspaceList
                menus={menus}
                menuGroups={menuGroups}
                currentSubMenu={currentSubMenu}
              />
            </div>
            <div className="workspace-submenu-favorites w-full mt-[24px]">
              <FavoritesList />
            </div>
          </>
        ) : null}
      </Space>
    </Skeleton>
  );
};

export { IWorkspaceListItem, IWorkspaceMenuGroup };
